// Create Campaign page component for InfluenceHub MVP
// Form for brands to create new campaigns with title, bio, collaboration type, budget, deadline, and optional physical gift

import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { collection, addDoc, serverTimestamp, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import Navbar from '../../components/layout/Navbar';
import Sidebar from '../../components/layout/Sidebar';
import DeliverablesMultiSelect from '../../components/forms/DeliverablesMultiSelect.jsx';
import { event as trackEvent } from '../../analytics/ga';

const REVISION_POLICY = "InfluenceHub includes 1 revision within the initial budget. Additional revisions require an extra fee.";

const CreateCampaign = ({ onNavigate }) => {
  const { user, profile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [deliverables, setDeliverables] = useState([]);
  const [collaborationType, setCollaborationType] = useState('paid'); // 'paid' or 'gifted'
  const [includesGiftedProduct, setIncludesGiftedProduct] = useState(false);
  
  // Analytics: Prevent duplicate event firing
  const campaignCreatedFired = useRef(false);
  
  const [formData, setFormData] = useState({
    title: '',
    bio: '',
    budgetUSD: '',
    deadline: '',
    productLink: '',
    shippingFromCountry: '',
    shippingNotes: ''
  });

  const countries = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'AU', label: 'Australia' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' },
    { value: 'NL', label: 'Netherlands' },
    { value: 'SE', label: 'Sweden' },
    { value: 'NO', label: 'Norway' },
    { value: 'DK', label: 'Denmark' },
    { value: 'FI', label: 'Finland' },
    { value: 'JP', label: 'Japan' },
    { value: 'KR', label: 'South Korea' },
    { value: 'SG', label: 'Singapore' },
    { value: 'HK', label: 'Hong Kong' },
    { value: 'NZ', label: 'New Zealand' },
    { value: 'BR', label: 'Brazil' },
    { value: 'MX', label: 'Mexico' },
    { value: 'AR', label: 'Argentina' },
    { value: 'IN', label: 'India' },
    { value: 'CN', label: 'China' },
    { value: 'RU', label: 'Russia' },
    { value: 'ZA', label: 'South Africa' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox separately
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      
      // Clear shipping fields when checkbox is unchecked
      if (name === 'hasPhysicalGift' && !checked) {
        setFormData(prev => ({
          ...prev,
          shippingFromCountry: '',
          shippingNotes: ''
        }));
        // Clear shipping errors
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.shippingFromCountry;
          return newErrors;
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Title: required, 3-100 characters
    const titleTrimmed = formData.title.trim();
    if (!titleTrimmed) {
      newErrors.title = 'Title is required';
    } else if (titleTrimmed.length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    } else if (titleTrimmed.length > 100) {
      newErrors.title = 'Title must be no more than 100 characters';
    }
    
    // Bio: required, 30-600 characters
    const bioTrimmed = formData.bio.trim();
    if (!bioTrimmed) {
      newErrors.bio = 'Bio is required';
    } else if (bioTrimmed.length < 30) {
      newErrors.bio = 'Bio must be at least 30 characters';
    } else if (bioTrimmed.length > 600) {
      newErrors.bio = 'Bio must be no more than 600 characters';
    }
    
    // Deliverables: required, at least one must be selected
    if (!deliverables || deliverables.length === 0) {
      newErrors.deliverables = 'Select at least one deliverable';
    }
    
    // Budget: required for paid collaborations, must be >= $10
    if (collaborationType === 'paid') {
    if (!formData.budgetUSD || formData.budgetUSD.trim() === '') {
        newErrors.budgetUSD = 'Budget is required for paid collaborations';
    } else {
      const budgetValue = parseFloat(formData.budgetUSD);
        if (isNaN(budgetValue) || budgetValue < 10) {
          newErrors.budgetUSD = 'For paid collaborations, the minimum budget is $10.';
        }
      }
    }
    
    // Shipping: required for gifted collaborations or if includesGiftedProduct is true
    const requiresShipping = collaborationType === 'gifted' || (collaborationType === 'paid' && includesGiftedProduct);
    if (requiresShipping && !formData.shippingFromCountry) {
      newErrors.shippingFromCountry = 'Shipping country is required when a product is involved';
    }
    
    // Deadline: required, free-form text
    if (!formData.deadline || formData.deadline.trim() === '') {
      newErrors.deadline = 'Content submission deadline is required';
    } else if (formData.deadline.trim().length < 3) {
      newErrors.deadline = 'Please provide a more detailed deadline description';
    }
    
    // Product Link: optional, free-form text field (max 500 characters)
    if (formData.productLink && formData.productLink.trim().length > 500) {
      newErrors.productLink = 'Product link must be 500 characters or less';
    }
    
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !profile) {
      alert('Please sign in to create a campaign');
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Fetch brand profile to denormalize name
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const brandProfile = userDoc.exists() ? userDoc.data() : {};

      const brandName = brandProfile.companyName || profile.companyName || profile.displayName || profile.name || null;

      // Determine collaboration type for Firestore
      const firestoreCollaborationType = collaborationType === 'paid' 
        ? (includesGiftedProduct ? 'Paid Collaboration with Gift' : 'Paid Collaboration')
        : 'Gifted Product';

      // Create campaign document
      const campaignData = {
        brandId: user.uid,
        brandName: brandName,
        title: formData.title.trim(),
        bio: formData.bio.trim(),
        collaborationType: firestoreCollaborationType,
        deliverables: deliverables, // Array of deliverable keys
        budgetUSD: collaborationType === 'paid' ? parseFloat(formData.budgetUSD) : 0,
        deadline: formData.deadline.trim(), // Store as string instead of Timestamp
        productLink: formData.productLink?.trim() || null,
        hasPhysicalGift: collaborationType === 'gifted' || includesGiftedProduct,
        shippingFromCountry: (collaborationType === 'gifted' || includesGiftedProduct) ? formData.shippingFromCountry : null,
        shippingNotes: (collaborationType === 'gifted' || includesGiftedProduct) ? (formData.shippingNotes?.trim() || null) : null,
        revisionPolicy: REVISION_POLICY,
        status: 'pending_approval',
        appliedCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'campaigns'), campaignData);
      
      console.log('✅ Campaign created with ID:', docRef.id);
      
      // Analytics: Track campaign_created event after successful campaign creation
      // Event triggered: campaign_created - when a campaign is successfully created in Firestore
      // Use ref to prevent duplicate firing
      if (!campaignCreatedFired.current) {
        campaignCreatedFired.current = true;
        try {
          trackEvent('campaign_created', {
            campaign_id: docRef.id,
          });
        } catch (err) {
          // Analytics must never block campaign creation
          console.warn('[CreateCampaign] Analytics error:', err);
        }
      }
      
      // Redirect to confirmation page
      onNavigate('campaign-submitted');
      
    } catch (error) {
      console.error('❌ Error creating campaign:', error);
      alert('Error creating campaign. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if shipping fields should be shown
  const showShippingFields = collaborationType === 'gifted' || (collaborationType === 'paid' && includesGiftedProduct);
  const showBudgetField = collaborationType === 'paid';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation bar */}
      <Navbar onNavigate={onNavigate} />
      
      <div className="flex">
        {/* Sidebar navigation */}
        <Sidebar onNavigate={onNavigate} />
        
        {/* Main content area */}
        <main className="flex-1 p-6">
          <div className="max-w-2xl mx-auto">
            {/* Page header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Create New Campaign</h1>
              <p className="mt-2 text-gray-600">
                Create a new influencer marketing campaign.
              </p>
            </div>

            {/* Campaign creation form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white rounded-xl shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Campaign Details</h2>
                
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      minLength={3}
                      maxLength={100}
                      aria-invalid={!!errors.title}
                      aria-describedby={errors.title ? 'title-error' : undefined}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.title ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter campaign title"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.title.length}/100 characters
                    </p>
                    {errors.title && (
                      <p id="title-error" className="mt-1 text-sm text-rose-600" role="alert">
                        {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Bio */}
                  <div>
                    <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                      Bio *
                    </label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      required
                      minLength={30}
                      maxLength={600}
                      rows={4}
                      aria-invalid={!!errors.bio}
                      aria-describedby={errors.bio ? 'bio-error' : undefined}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.bio ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Describe your campaign goals, target audience, and key messaging (30-600 characters)"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.bio.length}/600 characters
                    </p>
                    {errors.bio && (
                      <p id="bio-error" className="mt-1 text-sm text-rose-600" role="alert">
                        {errors.bio}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Collaboration Type - Radio Buttons */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Collaboration Type *
                      </label>
                      <div className="space-y-3">
                        <div className="border border-gray-300 rounded-md">
                          <label className="flex items-center space-x-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="radio"
                        name="collaborationType"
                              value="paid"
                              checked={collaborationType === 'paid'}
                              onChange={(e) => {
                                setCollaborationType(e.target.value);
                                // Reset includesGiftedProduct when switching away from paid
                                if (e.target.value !== 'paid') {
                                  setIncludesGiftedProduct(false);
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-gray-900">Paid Collaboration</span>
                              <p className="text-xs text-gray-500">Influencer receives payment for content</p>
                            </div>
                          </label>
                          {/* Includes Gifted Product Checkbox - nested under Paid Collaboration */}
                          {collaborationType === 'paid' && (
                            <div className="pl-11 pr-3 pb-3 border-t border-gray-200">
                              <label className="flex items-center space-x-3 pt-3">
                                <input
                                  type="checkbox"
                                  checked={includesGiftedProduct}
                                  onChange={(e) => setIncludesGiftedProduct(e.target.checked)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-700">
                                    Includes gifted product for creator
                                  </span>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    Check this if you're also sending a physical product in addition to payment
                                  </p>
                                </div>
                              </label>
                            </div>
                          )}
                        </div>
                        <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="radio"
                            name="collaborationType"
                            value="gifted"
                            checked={collaborationType === 'gifted'}
                            onChange={(e) => {
                              setCollaborationType(e.target.value);
                              // Reset includesGiftedProduct when switching away from paid
                              if (e.target.value !== 'paid') {
                                setIncludesGiftedProduct(false);
                              }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900">Gifted Product</span>
                            <p className="text-xs text-gray-500">Influencer receives product only (no payment)</p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Deliverables */}
                    <div className="md:col-span-2">
                      <DeliverablesMultiSelect
                        value={deliverables}
                        onChange={setDeliverables}
                        error={errors.deliverables}
                      />
                    </div>

                    {/* Budget (USD) - Only shown for Paid */}
                    {showBudgetField && (
                    <div>
                      <label htmlFor="budgetUSD" className="block text-sm font-medium text-gray-700 mb-2">
                        Budget (USD) *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          id="budgetUSD"
                          name="budgetUSD"
                          value={formData.budgetUSD}
                          onChange={handleInputChange}
                          required={showBudgetField}
                          min="10"
                          step="1"
                          aria-invalid={!!errors.budgetUSD}
                          aria-describedby={errors.budgetUSD ? 'budgetUSD-error' : undefined}
                          className={`w-full pl-8 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.budgetUSD ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="10"
                        />
                      </div>
                      {errors.budgetUSD && (
                        <p id="budgetUSD-error" className="mt-1 text-sm text-rose-600" role="alert">
                          {errors.budgetUSD}
                        </p>
                      )}
                    </div>
                    )}

                    {/* Content Submission Deadline */}
                    <div>
                      <label htmlFor="deadline" className="block text-sm font-medium text-gray-700 mb-2">
                        Content Submission Deadline *
                      </label>
                      <input
                        type="text"
                        id="deadline"
                        name="deadline"
                        value={formData.deadline}
                        onChange={handleInputChange}
                        required
                        aria-invalid={!!errors.deadline}
                        aria-describedby={errors.deadline ? 'deadline-error' : 'deadline-help'}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.deadline ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="e.g., 2 weeks after product delivery, Within 30 days of acceptance, Dec 15, 2025"
                      />
                      <p id="deadline-help" className="mt-1 text-xs text-gray-500">
                        Describe when influencers should submit their content (e.g., "2 weeks after product delivery" or a specific date)
                      </p>
                      {errors.deadline && (
                        <p id="deadline-error" className="mt-1 text-sm text-rose-600" role="alert">
                          {errors.deadline}
                        </p>
                      )}
                    </div>

                    {/* Product Link (optional) */}
                    <div className="md:col-span-2">
                      <label htmlFor="productLink" className="block text-sm font-medium text-gray-700 mb-2">
                        Product Link
                        <span className="ml-2 text-xs text-gray-500 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        id="productLink"
                        name="productLink"
                        value={formData.productLink}
                        onChange={handleInputChange}
                        maxLength={500}
                        aria-invalid={!!errors.productLink}
                        aria-describedby={errors.productLink ? 'productLink-error' : undefined}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.productLink ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="https://example.com/product"
                      />
                      {errors.productLink && (
                        <p id="productLink-error" className="mt-1 text-sm text-rose-600" role="alert">
                          {errors.productLink}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Shipping Fields (conditional) - Shown for Gifted or Paid + Gifted */}
                  {showShippingFields && (
                    <div className="space-y-4 pt-4 border-t border-gray-200">
                      <h3 className="text-sm font-medium text-gray-700">Shipping Information</h3>
                      
                      {/* Shipping From Country */}
                      <div>
                        <label htmlFor="shippingFromCountry" className="block text-sm font-medium text-gray-700 mb-2">
                          Shipping From Country *
                        </label>
                        <select
                          id="shippingFromCountry"
                          name="shippingFromCountry"
                          value={formData.shippingFromCountry}
                          onChange={handleInputChange}
                          required={showShippingFields}
                          aria-invalid={!!errors.shippingFromCountry}
                          aria-describedby={errors.shippingFromCountry ? 'shippingFromCountry-error' : undefined}
                          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.shippingFromCountry ? 'border-red-300' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select country</option>
                          {countries.map(country => (
                            <option key={country.value} value={country.value}>
                              {country.label}
                            </option>
                          ))}
                        </select>
                        {errors.shippingFromCountry && (
                          <p id="shippingFromCountry-error" className="mt-1 text-sm text-rose-600" role="alert">
                            {errors.shippingFromCountry}
                          </p>
                        )}
                      </div>

                      {/* Shipping Notes (optional) */}
                      <div>
                        <label htmlFor="shippingNotes" className="block text-sm font-medium text-gray-700 mb-2">
                          Shipping Notes
                          <span className="ml-2 text-xs text-gray-500 font-normal">(optional)</span>
                        </label>
                        <textarea
                          id="shippingNotes"
                          name="shippingNotes"
                          value={formData.shippingNotes}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Any additional shipping instructions or notes"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Revision Policy */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    <strong>Revision Policy:</strong> {REVISION_POLICY}
                  </p>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => onNavigate('dashboard')}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Creating Campaign...' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CreateCampaign;
